import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dyn } from "trader.ts/ui";
import { selectbox } from "trader.ts/ui.common";
import {
  instrumentid,
  internalteacherid,
  teacherid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as planningstyle from "../../../styles/planning.css.classes";
import * as style from "../../../styles/style.css.classes";
import { period } from "../../domain/periods";
import { getFirstWeekdayFrom, getMonday } from "../../lib/time";
import * as routes from "../../routes";
import { translateMonth } from "../../translations_specific";
import { planningDateKind } from "../planning_start_public/client";
import { planningDisplayMode, planningFilter } from "./client";
import { range } from "../../lib/range";
import { isNil } from "../../lib/istype";

const allDisplaySlotModes: [planningDisplayMode, string][] = [
  ["names", "Namen"],
  ["formula", "Formule"],
  ["instrument", "Cursus"],
  ["teacher", "Docent"],
  ["room", "Lokaal"],
  ["comment", "Opmerking"],
];

export function renderAside(
  reloadSlots: () => void,
  allInternalTeachers: {
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  }[],
  allInstruments: {
    id: instrumentid;
    description: string;
  }[],
  days: { date: joda.LocalDate; kind: planningDateKind }[],
  period: period,
  displaySlotsModeS: Source<planningDisplayMode[]>,
  filterS: Source<planningFilter>,
  teacherMode: { teacherid: teacherid; onlyEditExistingLgs: boolean } | null,
): HTMLElement {
  const content = renderFilters(
    displaySlotsModeS,
    filterS,
    reloadSlots,
    days,
    period,
    allInternalTeachers,
    allInstruments,
    teacherMode,
  );

  return (
    <aside style="max-height: var(--viewportminusnav); overflow-y: auto; border-left: 1px solid var(--grey-300); padding: 0px;">
      {content}
    </aside>
  );
}

function renderFilters(
  displaySlotsModeS: Source<planningDisplayMode[]>,
  filterS: Source<planningFilter>,
  reloadSlots: () => void,
  days: { date: joda.LocalDate; kind: planningDateKind }[],
  period: period,
  allInternalTeachers: {
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  }[],
  allInstruments: { id: instrumentid; description: string }[],
  teacherMode: { teacherid: teacherid; onlyEditExistingLgs: boolean } | null,
) {
  function toggleDisplayMode(ev: MouseEvent, m: planningDisplayMode) {
    if (ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey) {
      const currDisplayModes = displaySlotsModeS.get();
      const newDisplayModes = (
        currDisplayModes.includes(m)
          ? currDisplayModes.filter((dm) => dm !== m)
          : currDisplayModes.concat(m)
      ).orderBy((dm) => {
        return allDisplaySlotModes.findIndex((dms) => dms[0] === dm);
      }, "asc");
      displaySlotsModeS.set(newDisplayModes);
    } else {
      displaySlotsModeS.set([m]);
    }
    void rpc(
      routes.setPlanningDisplayModeCookieSpec,
      undefined,
      displaySlotsModeS.get(),
    );
  }
  function setFilter(pf: planningFilter) {
    filterS.set(pf);
    void rpc(routes.setPlanningFilterCookieSpec, undefined, pf);
    reloadSlots();
  }

  const labelStyle = `color: var(--grey-900); font-weight: 600;`;

  // We blank out allInstruments
  const dontShowTeacherAndInstrumentFilters = !isNil(teacherMode);

  return [
    renderPlanningDateButtons(
      period,
      days.first()!.date,
      days.last()!.date,
      (d) =>
        routes.planningRoute.link({
          teacher: undefined,
          time: undefined,
          date: d || undefined,
        }),
      {
        style: `height: 112px; padding: 16px; margin-bottom: 32px; border-bottom: 1px solid var(--grey-200);`,
      },
    ),

    <div
      style={`overflow: auto; max-height: calc(100% - 112px - 32px); padding: 16px;`}
    >
      <section>
        <h1 style={labelStyle}>{"Tekst"}</h1>
        {dyn(displaySlotsModeS, (activeMode) =>
          allDisplaySlotModes.map(([m, label]) => {
            return (
              <label
                className={style.checkbox + planningstyle.checkbox_as_button}
                style="display: inline-block;width: 49%; margin-right: 2px;"
              >
                <input
                  type="checkbox"
                  checked={activeMode.includes(m)}
                  onclick={(ev) => toggleDisplayMode(ev, m)}
                />
                <span>{label}</span>
              </label>
            );
          }),
        )}
      </section>
      {dyn(filterS, function (filter) {
        if (dontShowTeacherAndInstrumentFilters) {
          return <span></span>;
        }
        const teachers = (
          <section>
            <h1 style={labelStyle}>{"Docenten"}</h1>
            {allInternalTeachers.map((t) => {
              return (
                <label
                  className={style.checkbox + planningstyle.checkbox_as_button}
                >
                  <input
                    type="checkbox"
                    checked={
                      filter.tag === "teachers" &&
                      filter.ids.includes(t.internalteacherid)
                    }
                    onclick={(ev) => {
                      if (ev.ctrlKey || ev.metaKey || ev.altKey) {
                        const curr = filterS.get();
                        if (curr.tag === "instruments") {
                          setFilter({
                            tag: "teachers",
                            ids: [t.internalteacherid],
                          });
                        } else {
                          if (curr.ids.includes(t.internalteacherid)) {
                            setFilter({
                              tag: "teachers",
                              ids: curr.ids.filter(
                                (id) => id !== t.internalteacherid,
                              ),
                            });
                          } else {
                            setFilter({
                              tag: "teachers",
                              ids: curr.ids.concat(t.internalteacherid).uniq(),
                            });
                          }
                        }
                      } else {
                        setFilter({
                          tag: "teachers",
                          ids: [t.internalteacherid],
                        });
                      }
                    }}
                  />
                  <span>{t.firstname + " " + t.lastname}</span>
                </label>
              );
            })}

            <label
              className={style.checkbox + planningstyle.checkbox_as_button}
            >
              <input
                type="checkbox"
                checked={
                  filter.tag === "teachers" &&
                  filter.ids.length === allInternalTeachers.length
                }
                onclick={() => {
                  setFilter({
                    tag: "teachers",
                    ids: allInternalTeachers.map((i) => i.internalteacherid),
                  });
                }}
              />
              <span>{"Allemaal"}</span>
            </label>

            <div style="font-size: 13px;padding-top: 0px;">
              {"Ctrl + click om meerdere te selecteren"}
            </div>
          </section>
        );
        const instruments = (
          <section>
            <h1 style={labelStyle}>{"Docenten voor cursus"}</h1>
            {allInstruments.map((instr) => {
              return (
                <label
                  className={style.checkbox + planningstyle.checkbox_as_button}
                >
                  <input
                    type="checkbox"
                    checked={
                      filter.tag === "instruments" &&
                      filter.ids.includes(instr.id)
                    }
                    onclick={(ev) => {
                      if (ev.ctrlKey || ev.metaKey || ev.altKey) {
                        const curr = filterS.get();
                        if (curr.tag === "teachers") {
                          setFilter({
                            tag: "instruments",
                            ids: [instr.id],
                          });
                        } else {
                          if (curr.ids.includes(instr.id)) {
                            setFilter({
                              tag: "instruments",
                              ids: curr.ids.filter((id) => id !== instr.id),
                            });
                          } else {
                            setFilter({
                              tag: "instruments",
                              ids: curr.ids.concat(instr.id).uniq(),
                            });
                          }
                        }
                      } else {
                        setFilter({
                          tag: "instruments",
                          ids: [instr.id],
                        });
                      }
                    }}
                  />
                  <span>{instr.description}</span>
                </label>
              );
            })}
          </section>
        );
        return [teachers, instruments];
      })}
    </div>,
  ];
}

export function renderPlanningDateButtons(
  period: period,
  monday: joda.LocalDate,
  sunday: joda.LocalDate,
  mkLink: (d: joda.LocalDate | null) => string,
  opts?: { style?: string },
) {
  const previousWeekMonday = monday.minusDays(7);
  const previousWeekFriday = sunday.minusDays(7);
  const nextWeekMonday = monday.plusDays(7);
  const previousWeekLink =
    period.firstday.isBefore(previousWeekFriday) ||
    period.firstday.isEqual(previousWeekFriday)
      ? mkLink(previousWeekMonday)
      : undefined;
  const nextWeekLink = period.lastday.isAfter(nextWeekMonday)
    ? mkLink(nextWeekMonday)
    : undefined;

  const renderDate = (d: joda.LocalDate) =>
    d.dayOfMonth() + " " + translateMonth(d.month()).slice(0, 3).toLowerCase();

  const currentWeekI = Math.floor(
    (sunday.toEpochDay() - getMonday(period.firstday).toEpochDay()) / 7,
  );

  const weekS = new Source(currentWeekI.toString());
  const amountOfWeeksInPeriod = Math.ceil(
    (getFirstWeekdayFrom(period.lastday, joda.DayOfWeek.SUNDAY).toEpochDay() -
      getMonday(period.firstday).toEpochDay()) /
      7,
  );

  return (
    <section style={opts?.style || ""}>
      <div style="display: flex; align-items: center; margin-bottom: 4px">
        <a href={previousWeekLink}>
          <button
            className={style.button + style.button_has_only_icon}
            disabled={previousWeekLink === undefined}
            style="margin-bottom: 0px;"
          >
            <i className={FA.icon_arrow_left}></i>
          </button>
        </a>
        <div style="flex: 1; text-align: center">
          <div style=" font-weight: 600;">
            {renderDate(monday) + " - " + renderDate(sunday)}
          </div>
        </div>
        <a href={nextWeekLink}>
          <button
            className={style.button + style.button_has_only_icon}
            disabled={nextWeekLink === undefined}
            style="margin-bottom: 0px;"
          >
            <i className={FA.icon_arrow_right}></i>
          </button>
        </a>
      </div>
      <div className={style.equal_columns_non_responsive}>
        <div style="margin-right: 8px">
          <a href={mkLink(null)}>
            <button
              style={` width: 100%; padding: 6px; font-size: 13px; `}
              className={style.button_em_down}
            >
              {"Vandaag"}
            </button>
          </a>
        </div>
        <div>
          {selectbox({
            source: weekS,
            options: range(0, amountOfWeeksInPeriod).map((i) => {
              const d = period.firstday.plusDays(i * 7);
              const d1 = getMonday(d);
              const d2 = getFirstWeekdayFrom(d, joda.DayOfWeek.SUNDAY);
              return {
                value: i.toString(),
                label:
                  "Week" +
                  " " +
                  (i + 1).toString() +
                  " (" +
                  renderDate(d1) +
                  " - " +
                  renderDate(d2) +
                  ")",
              };
            }),
            style: `height: 31px;
                      border-bottom: 1px solid var(--grey-300);
                      font-size: 13px;`,
            oninput: () => {
              const weekIndex = parseInt(weekS.get());
              if (weekIndex !== currentWeekI) {
                const targetDay = period.firstday.plusDays(weekIndex * 7);
                window.location.assign(mkLink(targetDay));
              }
            },
          })}
        </div>
      </div>
    </section>
  );
}
